import React, { useState, useEffect } from 'react';
import EventCard from './EventCard';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const EventGrid = ({ filters, searchQuery, isLocationEnabled }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);

  // Mock events data
  const mockEvents = [
    {
      id: 1,
      title: "Jazz Under the Stars",
      venue: "Lincoln Center",
      date: "2024-07-20",
      time: "7:30 PM",
      image: "https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=400&h=300&fit=crop",
      startingPrice: 55,
      category: "Music",
      distance: "2.3 miles",
      ticketsLeft: 23
    },
    {
      id: 2,
      title: "Broadway Musical Matinee",
      venue: "Times Square Theater",
      date: "2024-07-22",
      time: "2:00 PM",
      image: "https://images.pexels.com/photos/109669/pexels-photo-109669.jpeg?w=400&h=300&fit=crop",
      startingPrice: 89,
      category: "Theater",
      distance: "1.8 miles"
    },
    {
      id: 3,
      title: "Food Truck Festival",
      venue: "Washington Square Park",
      date: "2024-07-25",
      time: "11:00 AM",
      image: "https://images.pixabay.com/photo/2017/05/11/19/44/fresh-2305192_1280.jpg?w=400&h=300&fit=crop",
      startingPrice: 0,
      category: "Food & Drink",
      distance: "3.1 miles"
    },
    {
      id: 4,
      title: "Stand-Up Comedy Night",
      venue: "Comedy Cellar",
      date: "2024-07-28",
      time: "9:00 PM",
      image: "https://images.unsplash.com/photo-1585699255208-f95d3286c0d2?w=400&h=300&fit=crop",
      startingPrice: 25,
      category: "Comedy",
      distance: "1.5 miles",
      ticketsLeft: 8
    },
    {
      id: 5,
      title: "Art Gallery Opening",
      venue: "MoMA",
      date: "2024-08-01",
      time: "6:00 PM",
      image: "https://images.pexels.com/photos/1839919/pexels-photo-1839919.jpeg?w=400&h=300&fit=crop",
      startingPrice: 35,
      category: "Art & Culture",
      distance: "2.7 miles"
    },
    {
      id: 6,
      title: "Basketball Championship",
      venue: "Madison Square Garden",
      date: "2024-08-05",
      time: "7:00 PM",
      image: "https://images.pixabay.com/photo/2016/11/29/13/14/attractive-1869761_1280.jpg?w=400&h=300&fit=crop",
      startingPrice: 125,
      category: "Sports",
      distance: "2.1 miles"
    },
    {
      id: 7,
      title: "Wine Tasting Experience",
      venue: "Brooklyn Winery",
      date: "2024-08-08",
      time: "5:00 PM",
      image: "https://images.unsplash.com/photo-1510812431401-41d2bd2722f3?w=400&h=300&fit=crop",
      startingPrice: 45,
      category: "Food & Drink",
      distance: "4.2 miles"
    },
    {
      id: 8,
      title: "Indie Rock Concert",
      venue: "Brooklyn Bowl",
      date: "2024-08-12",
      time: "8:00 PM",
      image: "https://images.pexels.com/photos/1105666/pexels-photo-1105666.jpeg?w=400&h=300&fit=crop",
      startingPrice: 40,
      category: "Music",
      distance: "3.8 miles",
      ticketsLeft: 15
    }
  ];

  useEffect(() => {
    loadEvents();
  }, [filters, searchQuery, isLocationEnabled]);

  const loadEvents = () => {
    setLoading(true);
    
    // Simulate API call
    setTimeout(() => {
      let filteredEvents = [...mockEvents];
      
      // Apply filters
      if (filters?.category && filters?.category !== 'all') {
        filteredEvents = filteredEvents?.filter(event => 
          event?.category?.toLowerCase()?.includes(filters?.category?.toLowerCase())
        );
      }
      
      if (filters?.price) {
        switch (filters?.price) {
          case 'free':
            filteredEvents = filteredEvents?.filter(event => event?.startingPrice === 0);
            break;
          case 'under25':
            filteredEvents = filteredEvents?.filter(event => event?.startingPrice < 25);
            break;
          case 'under50':
            filteredEvents = filteredEvents?.filter(event => event?.startingPrice < 50);
            break;
          case 'under100':
            filteredEvents = filteredEvents?.filter(event => event?.startingPrice < 100);
            break;
        }
      }
      
      // Apply search
      if (searchQuery) {
        filteredEvents = filteredEvents?.filter(event =>
          event?.title?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
          event?.venue?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
          event?.category?.toLowerCase()?.includes(searchQuery?.toLowerCase())
        );
      }
      
      // Sort by location if enabled
      if (isLocationEnabled) {
        filteredEvents?.sort((a, b) => {
          const distanceA = parseFloat(a?.distance?.split(' ')?.[0] || '999');
          const distanceB = parseFloat(b?.distance?.split(' ')?.[0] || '999');
          return distanceA - distanceB;
        });
      }
      
      setEvents(filteredEvents);
      setLoading(false);
      setPage(1);
      setHasMore(filteredEvents?.length > 8);
    }, 500);
  };

  const loadMoreEvents = () => {
    setLoading(true);
    
    // Simulate loading more events
    setTimeout(() => {
      const additionalEvents = mockEvents?.slice(0, 4)?.map(event => ({
        ...event,
        id: event?.id + 100,
        title: `${event?.title} - Extended`
      }));
      
      setEvents(prev => [...prev, ...additionalEvents]);
      setPage(prev => prev + 1);
      setHasMore(false);
      setLoading(false);
    }, 1000);
  };

  const LoadingSkeleton = () => (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
      {[...Array(8)]?.map((_, index) => (
        <div key={index} className="bg-card rounded-lg shadow-elevation-2 overflow-hidden animate-pulse">
          <div className="h-48 bg-muted"></div>
          <div className="p-4 space-y-3">
            <div className="h-4 bg-muted rounded w-3/4"></div>
            <div className="h-3 bg-muted rounded w-1/2"></div>
            <div className="h-3 bg-muted rounded w-2/3"></div>
            <div className="flex justify-between items-center">
              <div className="h-4 bg-muted rounded w-1/4"></div>
              <div className="h-8 bg-muted rounded w-20"></div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  if (loading && events?.length === 0) {
    return <LoadingSkeleton />;
  }

  if (events?.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
          <Icon name="Calendar" size={32} className="text-muted-foreground" />
        </div>
        <h3 className="text-lg font-semibold text-foreground mb-2">No events found</h3>
        <p className="text-muted-foreground mb-4">
          Try adjusting your filters or search terms to find more events.
        </p>
        <Button
          variant="outline"
          onClick={() => window.location?.reload()}
          iconName="RotateCcw"
        >
          Reset Search
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Events Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {events?.map((event) => (
          <EventCard key={event?.id} event={event} />
        ))}
      </div>
      {/* Load More */}
      {hasMore && (
        <div className="text-center">
          <Button
            variant="outline"
            size="lg"
            onClick={loadMoreEvents}
            loading={loading}
            iconName="ChevronDown"
            className="min-w-32"
          >
            {loading ? 'Loading...' : 'Load More Events'}
          </Button>
        </div>
      )}
      {/* Loading More Skeleton */}
      {loading && events?.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {[...Array(4)]?.map((_, index) => (
            <div key={index} className="bg-card rounded-lg shadow-elevation-2 overflow-hidden animate-pulse">
              <div className="h-48 bg-muted"></div>
              <div className="p-4 space-y-3">
                <div className="h-4 bg-muted rounded w-3/4"></div>
                <div className="h-3 bg-muted rounded w-1/2"></div>
                <div className="h-3 bg-muted rounded w-2/3"></div>
                <div className="flex justify-between items-center">
                  <div className="h-4 bg-muted rounded w-1/4"></div>
                  <div className="h-8 bg-muted rounded w-20"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default EventGrid;